<template>
  <div class="showcase">
    <div class="showcase-header">
      <h1 class="showcase-title">BERT Design System</h1>
      <p class="showcase-subtitle">Comprehensive UI component library for BERT Community</p>
    </div>

    <!-- Buttons Section -->
    <section class="showcase-section">
      <h2 class="section-title">Buttons</h2>
      
      <div class="component-group">
        <h3>Variants</h3>
        <div class="component-row">
          <bert-button variant="primary">Primary</bert-button>
          <bert-button variant="secondary">Secondary</bert-button>
          <bert-button variant="accent">Accent</bert-button>
          <bert-button variant="success">Success</bert-button>
          <bert-button variant="warning">Warning</bert-button>
          <bert-button variant="error">Error</bert-button>
          <bert-button variant="neutral">Neutral</bert-button>
          <bert-button variant="ghost">Ghost</bert-button>
          <bert-button variant="outline">Outline</bert-button>
        </div>
      </div>

      <div class="component-group">
        <h3>Sizes</h3>
        <div class="component-row">
          <bert-button size="xs">Extra Small</bert-button>
          <bert-button size="sm">Small</bert-button>
          <bert-button size="md">Medium</bert-button>
          <bert-button size="lg">Large</bert-button>
          <bert-button size="xl">Extra Large</bert-button>
        </div>
      </div>

      <div class="component-group">
        <h3>States</h3>
        <div class="component-row">
          <bert-button>Normal</bert-button>
          <bert-button :loading="true">Loading</bert-button>
          <bert-button :disabled="true">Disabled</bert-button>
          <bert-button :full-width="true">Full Width</bert-button>
        </div>
      </div>
    </section>

    <!-- Inputs Section -->
    <section class="showcase-section">
      <h2 class="section-title">Inputs</h2>
      
      <div class="component-group">
        <h3>Basic Inputs</h3>
        <div class="component-grid">
          <bert-input 
            label="Username" 
            placeholder="Enter your username"
            v-model="sampleData.username"
          />
          <bert-input 
            label="Email" 
            type="email"
            placeholder="Enter your email"
            v-model="sampleData.email"
            :required="true"
          />
          <bert-input 
            label="Password" 
            type="password"
            placeholder="Enter your password"
            v-model="sampleData.password"
          />
          <bert-input 
            label="Disabled Input" 
            placeholder="This is disabled"
            :disabled="true"
          />
        </div>
      </div>

      <div class="component-group">
        <h3>Input Sizes</h3>
        <div class="component-grid">
          <bert-input label="Small Input" size="sm" placeholder="Small size" />
          <bert-input label="Medium Input" size="md" placeholder="Medium size" />
          <bert-input label="Large Input" size="lg" placeholder="Large size" />
        </div>
      </div>

      <div class="component-group">
        <h3>Input States</h3>
        <div class="component-grid">
          <bert-input 
            label="Normal Input" 
            placeholder="Normal state"
            v-model="sampleData.normal"
          />
          <bert-input 
            label="Error Input" 
            placeholder="Input with error"
            error="This field is required"
            v-model="sampleData.error"
          />
          <bert-input 
            label="Input with Hint" 
            placeholder="Input with hint"
            hint="This is a helpful hint"
            v-model="sampleData.hint"
          />
        </div>
      </div>
    </section>

    <!-- Cards Section -->
    <section class="showcase-section">
      <h2 class="section-title">Cards</h2>
      
      <div class="component-group">
        <h3>Card Variants</h3>
        <div class="component-grid">
          <bert-card title="Default Card" variant="default">
            <p>This is a default card with some content inside it.</p>
          </bert-card>
          
          <bert-card title="Elevated Card" variant="elevated">
            <p>This card has elevation with shadow effects.</p>
          </bert-card>
          
          <bert-card title="Outlined Card" variant="outlined">
            <p>This card has a prominent border outline.</p>
          </bert-card>
          
          <bert-card title="Filled Card" variant="filled">
            <p>This card has a filled background color.</p>
          </bert-card>
        </div>
      </div>

      <div class="component-group">
        <h3>Card with Custom Content</h3>
        <div class="component-grid">
          <bert-card variant="elevated" :hoverable="true">
            <template #header>
              <div style="display: flex; align-items: center; gap: 12px;">
                <div style="width: 40px; height: 40px; background: var(--primary-600); border-radius: 50%;"></div>
                <div>
                  <h4 style="margin: 0; font-size: 16px;">Custom Header</h4>
                  <p style="margin: 0; font-size: 14px; color: var(--neutral-500);">With avatar</p>
                </div>
              </div>
            </template>
            
            <p>This card demonstrates custom header content with an avatar and hoverable effect.</p>
            
            <template #footer>
              <bert-button size="sm" variant="primary">Action</bert-button>
              <bert-button size="sm" variant="ghost">Cancel</bert-button>
            </template>
          </bert-card>
        </div>
      </div>
    </section>

    <!-- Badges Section -->
    <section class="showcase-section">
      <h2 class="section-title">Badges</h2>
      
      <div class="component-group">
        <h3>Badge Variants</h3>
        <div class="component-row">
          <bert-badge variant="primary">Primary</bert-badge>
          <bert-badge variant="secondary">Secondary</bert-badge>
          <bert-badge variant="accent">Accent</bert-badge>
          <bert-badge variant="success">Success</bert-badge>
          <bert-badge variant="warning">Warning</bert-badge>
          <bert-badge variant="error">Error</bert-badge>
          <bert-badge variant="neutral">Neutral</bert-badge>
        </div>
      </div>

      <div class="component-group">
        <h3>Badge Sizes & Styles</h3>
        <div class="component-row">
          <bert-badge size="sm">Small</bert-badge>
          <bert-badge size="md">Medium</bert-badge>
          <bert-badge size="lg">Large</bert-badge>
          <bert-badge :pill="true">Pill Shape</bert-badge>
          <bert-badge :outline="true">Outline</bert-badge>
          <bert-badge variant="success" :pill="true" :outline="true">Pill Outline</bert-badge>
        </div>
      </div>
    </section>

    <!-- Alerts Section -->
    <section class="showcase-section">
      <h2 class="section-title">Alerts</h2>
      
      <div class="component-group">
        <h3>Alert Variants</h3>
        <div class="component-stack">
          <bert-alert 
            variant="primary" 
            title="Information" 
            message="This is an informational alert message."
            :closable="true"
          />
          <bert-alert 
            variant="success" 
            title="Success" 
            message="Your action was completed successfully."
            :closable="true"
          />
          <bert-alert 
            variant="warning" 
            title="Warning" 
            message="Please review this important information."
            :closable="true"
          />
          <bert-alert 
            variant="error" 
            title="Error" 
            message="An error occurred while processing your request."
            :closable="true"
          />
        </div>
      </div>

      <div class="component-group">
        <h3>Alert Styles</h3>
        <div class="component-stack">
          <bert-alert 
            variant="primary" 
            message="Standard filled alert with icon."
          />
          <bert-alert 
            variant="primary" 
            message="Outline alert style."
            :outline="true"
          />
          <bert-alert 
            variant="success" 
            message="Alert without icon."
            :show-icon="false"
          />
        </div>
      </div>
    </section>

    <!-- Loading Section -->
    <section class="showcase-section">
      <h2 class="section-title">Loading</h2>
      
      <div class="component-group">
        <h3>Loading Sizes</h3>
        <div class="component-row">
          <bert-loading size="sm" text="Small" />
          <bert-loading size="md" text="Medium" />
          <bert-loading size="lg" text="Large" />
          <bert-loading size="xl" text="Extra Large" />
        </div>
      </div>

      <div class="component-group">
        <h3>Loading Variants</h3>
        <div class="component-row">
          <bert-loading variant="primary" text="Primary" />
          <bert-loading variant="secondary" text="Secondary" />
          <bert-loading variant="neutral" text="Neutral" />
        </div>
      </div>
    </section>

    <!-- Modal Section -->
    <section class="showcase-section">
      <h2 class="section-title">Modal</h2>
      
      <div class="component-group">
        <h3>Modal Examples</h3>
        <div class="component-row">
          <bert-button @click="showModal = true">Open Modal</bert-button>
          <bert-button @click="showLargeModal = true" variant="secondary">Large Modal</bert-button>
        </div>
      </div>
    </section>

    <!-- Modals -->
    <bert-modal 
      :visible="showModal" 
      title="Sample Modal"
      @close="showModal = false"
    >
      <p>This is a sample modal dialog. You can put any content here.</p>
      <bert-input label="Sample Input" placeholder="Type something..." />
      
      <template #footer>
        <bert-button @click="showModal = false" variant="ghost">Cancel</bert-button>
        <bert-button @click="showModal = false">Save</bert-button>
      </template>
    </bert-modal>

    <bert-modal 
      :visible="showLargeModal" 
      title="Large Modal"
      size="lg"
      @close="showLargeModal = false"
    >
      <p>This is a larger modal with more space for content.</p>
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px;">
        <bert-input label="First Name" placeholder="Enter first name" />
        <bert-input label="Last Name" placeholder="Enter last name" />
        <bert-input label="Email" type="email" placeholder="Enter email" />
        <bert-input label="Phone" placeholder="Enter phone number" />
      </div>
      
      <template #footer>
        <bert-button @click="showLargeModal = false" variant="ghost">Cancel</bert-button>
        <bert-button @click="showLargeModal = false" variant="primary">Submit</bert-button>
      </template>
    </bert-modal>
  </div>
</template>

<script>
import BertButton from './ui/Button.vue'
import BertInput from './ui/Input.vue'
import BertCard from './ui/Card.vue'
import BertBadge from './ui/Badge.vue'
import BertAlert from './ui/Alert.vue'
import BertLoading from './ui/Loading.vue'
import BertModal from './ui/Modal.vue'

export default {
  name: 'ComponentShowcase',
  components: {
    BertButton,
    BertInput,
    BertCard,
    BertBadge,
    BertAlert,
    BertLoading,
    BertModal
  },
  data() {
    return {
      showModal: false,
      showLargeModal: false,
      sampleData: {
        username: '',
        email: '',
        password: '',
        normal: '',
        error: '',
        hint: ''
      }
    }
  }
}
</script>

<style scoped>
.showcase {
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--space-8);
  font-family: var(--font-family-sans);
}

.showcase-header {
  text-align: center;
  margin-bottom: var(--space-16);
}

.showcase-title {
  font-size: var(--text-4xl);
  font-weight: var(--font-bold);
  color: var(--neutral-800);
  margin: 0 0 var(--space-4) 0;
}

.showcase-subtitle {
  font-size: var(--text-lg);
  color: var(--neutral-600);
  margin: 0;
}

.showcase-section {
  margin-bottom: var(--space-16);
}

.section-title {
  font-size: var(--text-2xl);
  font-weight: var(--font-semibold);
  color: var(--neutral-800);
  margin: 0 0 var(--space-8) 0;
  padding-bottom: var(--space-4);
  border-bottom: 2px solid var(--primary-200);
}

.component-group {
  margin-bottom: var(--space-8);
}

.component-group h3 {
  font-size: var(--text-lg);
  font-weight: var(--font-medium);
  color: var(--neutral-700);
  margin: 0 0 var(--space-4) 0;
}

.component-row {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
  align-items: center;
}

.component-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--space-6);
}

.component-stack {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

@media (max-width: 768px) {
  .showcase {
    padding: var(--space-4);
  }
  
  .component-grid {
    grid-template-columns: 1fr;
  }
  
  .component-row {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>